import { Icon } from '@vrplatform/icons';
import { type ObjectJsonSchema, sortBy, toTitleCase } from '@vrplatform/utils';
import { Box, Stack, type StackProps, Text } from '@mantine/core';
import type { InputFormSchema } from '@vrplatform/ui-common';
import { type ReactElement, useMemo } from 'react';
import { FormProvider, type UseFormReturn } from 'react-hook-form';
import { InputByType } from './InputByType';
import type { FormInputValueChangeType } from './_components/_base-input-types';

export type InputsBySchemaProps = {
  schema: InputFormSchema;
  methods: UseFormReturn;
  hideFields?: string[];
  onInputValueChange?: FormInputValueChangeType;
};

export const LegacyInputsBySchema = ({
  schema,
  methods,
  hideFields = [],
  onInputValueChange,
  ...stackProps
}: InputsBySchemaProps & StackProps) => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const sortedSchema = useMemo(() => {
    return sortBy(
      Object.entries(schema).filter((x) => !hideFields.includes(x[0])),
      (x) => (x[1].optional ? 1 : 0)
    );
  }, [schema, ...hideFields]);

  return (
    <FormProvider {...methods}>
      <Stack mb={10} gap={12} {...stackProps}>
        {sortedSchema.map(([key, value]) => (
          <Box key={key}>
            <InputByType
              objectKey={key}
              value={value}
              onInputValueChange={onInputValueChange}
            />
            {value.description && (
              <Text
                size="xs"
                c={'gray'}
                component="p"
                m={0}
                mt={value.link ? 5 : 3}
              >
                {value.description}{' '}
                {value.link && (
                  <Box
                    pl={2}
                    component="a"
                    href={value.link}
                    target="_blank"
                    rel="noopener,noreferrer"
                    display="inline-flex"
                  >
                    <Icon
                      icon="ExternalLinkIcon"
                      color={({ primaryColor, colors }) =>
                        colors[primaryColor][6]
                      }
                      strokeWidth={2}
                      size={14}
                    />
                  </Box>
                )}
              </Text>
            )}
          </Box>
        ))}
      </Stack>
    </FormProvider>
  );
};

export type InputsByJSONSchemaProps = {
  ignoreKeys?: string[];
  schema: ObjectJsonSchema;
  methods: UseFormReturn;
  renderContainer?: (
    children: ReactElement<any, any>
  ) => ReactElement<any, any>;
};

export const InputsByJSONSchema = ({
  ignoreKeys,
  schema: jsonSchema,
  methods,
  renderContainer,
}: InputsByJSONSchemaProps) => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const schema = useMemo(() => {
    return jsonSchemaProperties(jsonSchema, ignoreKeys);
  }, [jsonSchema]);
  if (!methods || !schema) return null;

  const component = <LegacyInputsBySchema methods={methods} schema={schema} />;
  if (renderContainer) {
    return renderContainer(component) || null;
  }
  return component;
};

export function jsonSchemaProperties(
  jsonSchema: ObjectJsonSchema,
  ignoreKeys?: string[]
) {
  const schema: InputFormSchema = {};
  if (jsonSchema?.properties)
    for (const key in jsonSchema.properties) {
      if (ignoreKeys?.includes(key)) continue;
      const value = jsonSchema.properties[key];
      schema[key] = {
        type:
          key === 'month'
            ? 'month'
            : key === 'date'
              ? 'date'
              : (value.type as any),
        label: value.title || toTitleCase(key),
        description: value.description,
        optional: value.nullable,
        defaultValue: value.default,
        disabled: value.readOnly,
        placeholder: value.examples?.[0],
      };
    }
  if (Object.keys(schema).length === 0) return undefined;
  return schema;
}
// {
//   "$schema": "http://json-schema.org/draft-04/schema#",
//   "$id": "https://example.com/employee.schema.json",
//   "title": "Record of employee",
//   "description": "This document records the details of an employee",
//   "type": "object",
//   "properties": {
//       "date": {
//           "description": "The date to re-generate statements for",
//           "type": "string",
//           "format": "month"
//       }
//   }
// }
